import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { ApiWebSocketService, LoggerService } from '@shared';
import { VendorNativeAlarmService } from './vendor-native-alarm.service';
import { PendingOrderReminderService } from './pending-order-reminder.service';
import { FcmBackendRegistrationService } from './fcm-backend-registration.service';
import { VendorPushService } from './vendor-push.service';
import { VendorOrderRing } from './vendor-order-ring.plugin';

/**
 * Centralized vendor session teardown so logout / 401 clears rings, reminders,
 * FCM registration, push listeners, and WebSocket before (or when) auth is cleared.
 */
@Injectable({ providedIn: 'root' })
export class VendorSessionTeardownService {
  private readonly nativeAlarm = inject(VendorNativeAlarmService);
  private readonly pendingReminders = inject(PendingOrderReminderService);
  private readonly fcmBackend = inject(FcmBackendRegistrationService);
  private readonly vendorPush = inject(VendorPushService);
  private readonly ws = inject(ApiWebSocketService);
  private readonly logger = inject(LoggerService);

  private inFlight: Promise<void> | null = null;
  private tornDown = false;

  /**
   * Idempotent cleanup. Call before AuthService.signOut() while JWT may still be valid.
   */
  async teardown(reason: string): Promise<void> {
    if (this.tornDown) {
      this.logger.debug(`[VendorSessionTeardown] skip (already torn down) reason=${reason}`);
      return;
    }
    if (this.inFlight) {
      return this.inFlight;
    }

    this.inFlight = this.runTeardown(reason);
    try {
      await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }

  /** Mark session active after successful login (native FCM gate + allow future teardowns). */
  async markSessionActive(reason: string): Promise<void> {
    this.tornDown = false;
    if (Capacitor.isNativePlatform()) {
      try {
        await VendorOrderRing.setSessionActive({ active: true });
      } catch (e: unknown) {
        this.logger.warn(
          `[VendorSessionTeardown] setSessionActive(true) failed reason=${reason}: ${(e as Error)?.message ?? e}`,
        );
      }
    }
    this.logger.debug(`[VendorSessionTeardown] session active reason=${reason}`);
  }

  private async runTeardown(reason: string): Promise<void> {
    this.logger.debug(`[VendorSessionTeardown] start reason=${reason}`);
    this.nativeAlarm.stopAllSounds();
    this.nativeAlarm.clearWatch();
    try {
      await this.pendingReminders.stopAllReminders();
    } catch (e: unknown) {
      this.logger.warn(`[VendorSessionTeardown] stopAllReminders failed: ${(e as Error)?.message ?? e}`);
    }

    if (Capacitor.isNativePlatform()) {
      try {
        await VendorOrderRing.setSessionActive({ active: false });
      } catch (e: unknown) {
        this.logger.warn(
          `[VendorSessionTeardown] setSessionActive(false) failed: ${(e as Error)?.message ?? e}`,
        );
      }
    }

    try {
      await this.fcmBackend.unregisterToken();
    } catch (e: unknown) {
      this.logger.warn(`[VendorSessionTeardown] unregisterToken failed: ${(e as Error)?.message ?? e}`);
    }

    try {
      await this.vendorPush.teardownOnLogout();
    } catch (e: unknown) {
      this.logger.warn(`[VendorSessionTeardown] push teardown failed: ${(e as Error)?.message ?? e}`);
    }

    try {
      this.ws.disconnect();
    } catch (e: unknown) {
      this.logger.warn(`[VendorSessionTeardown] ws.disconnect failed: ${(e as Error)?.message ?? e}`);
    }

    this.tornDown = true;
    this.logger.debug(`[VendorSessionTeardown] done reason=${reason}`);
  }
}
